import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Modal, Scroller } from '@/shared/ui/Modal'
import { Select } from '@/shared/ui/Select'
import { Strip } from '@/shared/ui/Strip'
import { EditIcon, TrashIcon } from '@/shared/ui/icons'
import type { Currency, WalletResponse } from '@/shared/api/types'
import { coinOf, networkOf } from '@/shared/lib/currencies'
import { shortAddress } from '@/shared/lib/format'
import { useCabinet, useStores, useUi } from '@/stores/RootStore'
import { DialogPage } from './DialogPage'
import styles from './Wallets.module.css'
import { explain } from '@/shared/lib/errors'

/** Все валюты API в порядке макета — выбор при создании кошелька. */
const CURRENCY_OPTIONS: { value: Currency; label: string; sub: string }[] = (
  ['btc', 'ltc', 'xmr', 'gram', 'usdt_trc20', 'usdt_ton'] as Currency[]
).map((currency) => ({
  value: currency,
  label: `${coinOf(currency).ticker} (${networkOf(currency)})`,
  sub: coinOf(currency).label,
}))

/**
 * Кошельки для вывода.
 *
 * Экран короткий, но цена ошибки на нём максимальная: адрес отсюда
 * попадает в подписанную транзакцию. Поэтому адрес показывается
 * целиком в правке и не редактируется после создания.
 */
export const WalletsPage = observer(function WalletsPage() {
  const { wallets: store } = useStores()
  const cabinet = useCabinet()
  const resource = cabinet.wallets

  useEffect(() => { void resource.load() }, [resource])

  return (
    <>
    <DialogPage
      tall
      hidden={store.dialog.kind !== 'none'}
      title="Кошельки"
      note="Подключённые адреса для автоматического вывода средств."
      /* Без иконки: в макете у кнопки диалога только подпись. */
      actions={<Button variant="primary" onClick={() => store.openAdd()}>Добавить</Button>}
    >
      <>
        {resource.error && !resource.loaded ? (
          <Strip tone="bad" aside={<Button variant="secondary" onClick={() => void resource.load()}>Ещё раз</Button>}>
            {explain(resource.error)}
          </Strip>
        ) : null}

        {store.items.length === 0 && resource.loaded ? null : (
          <Scroller>
            <ul className={styles.list}>
              {store.items.map((wallet) => (
                <WalletRow key={wallet.id} wallet={wallet} />
              ))}
            </ul>
          </Scroller>
        )}

      </>
    </DialogPage>

    {/* Добавление, правка и удаление — по одному окну за раз: пока
        открыто своё, список спрятан (`hidden` выше) и возвращается,
        когда окно закрылось. */}
    <WalletDialogs />
    </>
  )
})

const WalletRow = observer(function WalletRow({ wallet }: { wallet: WalletResponse }) {
  const { wallets: store } = useStores()
  const coin = coinOf(wallet.currency)
  return (
    <li className={styles.item}>
      <span className={styles.body}>
        {/* Сеть в скобках — только у монет, живущих в нескольких сетях:
            «BTC (Bitcoin)» — шум, «USDT (TRC20)» — единственный способ
            не перепутать адреса, которые друг друга не принимают. */}
        <span className={styles.ticker}>
          {coin.networks.length > 1
            ? `${coin.ticker} (${networkOf(wallet.currency)})`
            : coin.ticker}
        </span>
        <span className={styles.addr}>{shortAddress(wallet.address, 4, 4)}</span>
        <span className={styles.name}>{wallet.name}</span>
      </span>
      <span className={styles.acts}>
        <Button variant="icon" aria-label={`Изменить «${wallet.name}»`}
                onClick={() => store.openEdit(wallet)}>
          <EditIcon />
        </Button>
        <Button variant="icon" tone="bad" aria-label={`Удалить «${wallet.name}»`}
                onClick={() => store.openRemove(wallet)}>
          <TrashIcon />
        </Button>
      </span>
    </li>
  )
})

const WalletDialogs = observer(function WalletDialogs() {
  const { wallets: store } = useStores()
  const ui = useUi()
  const dialog = store.dialog

  if (dialog.kind === 'none') return null

  if (dialog.kind === 'remove') {
    const used = store.rulesFor(dialog.wallet.id)
    return (
      <Modal title="Удалить кошелёк?"
             note={`Вы уверены, что хотите удалить кошелёк «${dialog.wallet.name}»?`}
             onClose={() => store.close()}
             actions={
               <>
                 <Button variant="secondary" onClick={() => store.close()}>
                   {used > 0 ? 'Закрыть' : 'Не удалять'}
                 </Button>
                 {used > 0 ? (
                   <Button variant="primary"
                           onClick={() => { store.close(); ui.openDialog({ kind: 'auto' }) }}>
                     К автовыводам
                   </Button>
                 ) : (
                   <Button variant="primary" busy={store.busy}
                           onClick={() => void store.remove()}>Да, удалить</Button>
                 )}
               </>
             }>
        {/*
          Кошелёк, на который ссылается правило, бекенд удалять
          отказывается. Показать это надо здесь, а не отказом после
          нажатия: иначе человек жмёт «Да, удалить», получает ошибку
          и не понимает, что именно ему теперь делать.
        */}
        {/* Только предупреждение, которое меняет исход. «История
            останется» — успокоительная строка, в макете её нет, а
            карточка из-за неё на 58px выше. Отказ бекенда из-за
            правила остаётся: без него человек жмёт «Да, удалить»,
            получает ошибку и не понимает, что делать. */}
        {used > 0 ? (
          <Strip tone="warn">
            Кошелёк используется в автовыводе
          </Strip>
        ) : null}
      </Modal>
    )
  }

  const adding = dialog.kind === 'add'
  return (
    <Modal title={adding ? 'Добавить кошелёк' : dialog.wallet.name}
           onClose={() => store.close()}
           actions={
             <>
               <Button variant="secondary" onClick={() => store.close()}>Отмена</Button>
               <Button variant="primary" busy={store.busy}
                       onClick={() => void store.submit()}>
                 {adding ? 'Добавить' : 'Сохранить'}
               </Button>
             </>
           }>
      {/* Поля — колонкой с зазором 16, как в макете (Frame 339208),
          а не детьми окна с зазором 32: разница копится по числу
          полей и делает карточку выше макета. */}
      <div className={styles.fields}>
        <Select label="Валюта" placeholder="Выберите валюту"
                options={CURRENCY_OPTIONS} value={store.draft.currency}
                onChange={(value) => store.patch({ currency: value as Currency })}
                disabled={!adding}
                error={store.errors.currency ?? null} />

        <Field label="Название" placeholder="Укажите название"
               value={store.draft.name} maxLength={64}
               onChange={(e) => store.patch({ name: e.target.value })}
               error={store.errors.name ?? null} />

        <Field label="Адрес" placeholder="Укажите адрес"
               value={store.draft.address} readOnly={!adding}
               onChange={(e) => store.patch({ address: e.target.value.trim() })}
               error={store.errors.address ?? null} />

        {/* Удаление — из самого окна правки, блоком «Действия»: так
            в макете (Wallets 4:34053, Edit). Отдельной кнопкой рядом
            с «Сохранить» его ставить нельзя — адрес вывода удаляют
            не мимоходом, и подтверждение спросят отдельным окном. */}
        {adding ? null : (
          <div className={styles.actionsBlock}>
            <span className={styles.actionsLabel}>Действия</span>
            <Button variant="secondary" block
                    onClick={() => store.openRemove(dialog.wallet)}>
              Удалить
            </Button>
          </div>
        )}
      </div>
    </Modal>
  )
})
